import { __rest, __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsx } from 'react/jsx-runtime';
import { toCamelCase, isObject } from '@websolutespa/bom-core';
import { useLabel } from '@websolutespa/bom-mixer-hooks';
import { createContext, useRef, useContext } from 'react';
import { toast } from 'sonner';
import { useStore, createStore } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LLM_BLOCKS } from '../llm/blocks/index.js';
import { LLM_COMPONENTS } from '../llm/components/index.js';
import { getThemes } from '../llm/theme.js';
import { getVars, createVars } from '../llm/vars.js';
import { LLM_DEFAULT_MAX_FILE_SIZE, LLM_DEFAULT_MIME_TYPES } from '../types.js';
import { ApiService } from './api.service.js';
import { MessageDecoder, MessageService } from './message.service.js';
import { getMock } from './mock.js';
import { Speech, chunksToTexts } from './speech.js';
import { formatBytes, fileToResizedBase64 } from './upload.js';
import { LlmViewProvider } from './useLlmView.js';

const createLlmStore = config => {
  const sanitizedConfig = (config.plugins || []).reduce((p, c) => {
    return c(p);
  }, Object.assign({}, config));
  const {
      apiKey,
      appKey,
      contexts,
      customTheme,
      endpoint,
      preview,
      test,
      threadId,
      decorateUrl,
      label,
      onAction
    } = sanitizedConfig,
    options = __rest(sanitizedConfig, ["apiKey", "appKey", "contexts", "customTheme", "endpoint", "preview", "test", "threadId", "decorateUrl", "label", "onAction"]);
  const theme = getThemes([customTheme], config.defaultTheme);
  const vars = getVars(theme);
  const props = {
    asideEnabled: false,
    blocks: Object.assign(Object.assign({}, LLM_BLOCKS), options.blocks),
    components: Object.assign(Object.assign({}, LLM_COMPONENTS), options.components),
    contexts,
    history: [],
    hydrated: false,
    locale: 'en',
    maxFileSize: LLM_DEFAULT_MAX_FILE_SIZE,
    messages: [],
    mimeTypes: LLM_DEFAULT_MIME_TYPES,
    prompt: '',
    ready: false,
    speakEnabled: true,
    storageMode: options.storageMode || 'local',
    storedHistory: [],
    streaming: false,
    systemContext: Object.assign({}, options.systemContext),
    theme,
    vars
  };
  let messageService = undefined;
  const speech = new Speech();
  const storeKey = toCamelCase(`llm-${appKey}`);
  const getHistory = function () {
    for (var _len = arguments.length, args_1 = new Array(_len), _key = 0; _key < _len; _key++) {
      args_1[_key] = arguments[_key];
    }
    return __awaiter(void 0, [...args_1], void 0, function (storedHistory) {
      if (storedHistory === void 0) {
        storedHistory = [];
      }
      return function* () {
        if (typeof options.history === 'object' && typeof options.history.get === 'function') {
          try {
            storedHistory = yield options.history.get();
          } catch (error) {
            console.log('useLlm.getHistory.error', error);
          }
        }
        return storedHistory;
      }();
    });
  };
  const addItemToHistory = item => __awaiter(void 0, void 0, void 0, function* () {
    if (typeof options.history === 'object' && typeof options.history.addItem === 'function') {
      try {
        yield options.history.addItem(item);
      } catch (error) {
        console.log('useLlm.addItemToHistory.error', error);
      }
    }
  });
  const removeItemFromHistory = item => __awaiter(void 0, void 0, void 0, function* () {
    if (typeof options.history === 'object' && typeof options.history.removeItem === 'function') {
      try {
        yield options.history.removeItem(item);
      } catch (error) {
        console.log('useLlm.removeItemFromHistory.error', error);
      }
    }
  });
  const useStore = createStore()(persist((set, get) => Object.assign(Object.assign({}, props), {
    actions: {
      getApi: () => {
        const state = get();
        const {
          locale,
          threadId
        } = state;
        const apiService = new ApiService({
          apiKey,
          appKey,
          threadId,
          endpoint,
          locale,
          test,
          preview
        });
        return apiService;
      },
      init: (locale, instance) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const state = get();
        const apiService = new ApiService({
          apiKey,
          appKey,
          threadId: threadId || state.threadId,
          endpoint,
          locale,
          test,
          preview
        });
        const app = yield apiService.getInfo();
        if (!app) {
          return;
        }
        const textToSpeechApiKey = app.contents.textToSpeechApiKey;
        const textToSpeechVoiceId = app.contents.textToSpeechVoiceId;
        const synthesisMode = app.contents.disableSpeechSynthesis ? 'none' : app.contents.synthesisMode || 'default';
        const recognitionMode = app.contents.disableSpeechRecognition ? 'none' : app.contents.recognitionMode || 'default';
        const speechEnabled = state.speakEnabled;
        speech.lang = locale;
        speech.enabled = speechEnabled;
        speech.textToSpeechApiKey = textToSpeechApiKey;
        speech.textToSpeechVoiceId = textToSpeechVoiceId;
        speech.synthesisMode = synthesisMode;
        speech.recognitionMode = recognitionMode;
        // console.log('useLlm.init', { textToSpeechApiKey, textToSpeechVoiceId, synthesisMode, recognitionMode, speechEnabled });
        speech.on('result', transcript => __awaiter(void 0, void 0, void 0, function* () {
          const state = get();
          state.actions.setPrompt(transcript);
        }));
        const thread = MessageDecoder.decodeThread(app.thread);
        const testTheme = test ? (_b = (_a = getMock(locale, test).app) === null || _a === void 0 ? void 0 : _a.contents) === null || _b === void 0 ? void 0 : _b.customTheme : {};
        /*
        console.log(
          'appTheme', app.contents.customTheme,
          'testTheme', testTheme,
          'customTheme', customTheme
        );
        */
        const theme = getThemes([app.contents.customTheme, testTheme, customTheme], config.defaultTheme);
        const mode = theme.mode;
        const storageMode = options.storageMode || app.contents.storageMode || 'local';
        const storedHistory = yield getHistory(state.storedHistory);
        const history = yield apiService.decorateHistory(storedHistory, mode);
        /*
        console.log(
          'theme', theme
        );
        */
        const vars = getVars(theme);
        createVars(vars);
        // console.log('thread', thread);
        const patchState = Object.assign({
          app,
          history,
          instance,
          locale,
          ready: true,
          recognitionMode,
          storageMode,
          storedHistory,
          synthesisMode,
          theme,
          threadId,
          vars,
          mimeTypes: app.contents.mimeTypes || LLM_DEFAULT_MIME_TYPES,
          maxFileSize: app.contents.maxFileSize || LLM_DEFAULT_MAX_FILE_SIZE
        }, thread);
        const newState = Object.assign(Object.assign({}, state), patchState);
        if (typeof sanitizedConfig.onInit === 'function') {
          sanitizedConfig.onInit(newState);
        }
        set(() => patchState);
      }),
      send: (prompt, onPrompt, onMessage, onEnd) => __awaiter(void 0, void 0, void 0, function* () {
        speech.cancel();
        const state = get();
        messageService = new MessageService({
          apiKey,
          appKey,
          endpoint,
          locale: state.locale,
          test
        });
        let uploaded;
        if (state.upload) {
          // https://platform-ai-dev.ws-deploy-01.wslabs.it/api/llmApp/68ac0bfed671c7c791c6b8ec?locale=it&draft=false&depth=1
          const apiService = new ApiService({
            apiKey,
            appKey,
            endpoint,
            locale: state.locale,
            preview,
            test,
            threadId
          });
          uploaded = yield apiService.upload(state.upload);
          if (!uploaded) {
            return;
          }
        }
        const messages = yield messageService.addUserMessage(state.messages, prompt, uploaded);
        set(state => ({
          chunks: [],
          message: undefined,
          messages,
          prompt: '',
          streaming: true,
          upload: undefined
        }));
        if (typeof onPrompt === 'function') {
          onPrompt();
        }
        const request = yield messageService.getRequest(messages, state);
        yield messageService.sendMessage(request,
        // onMessage
        response => {
          // console.log('messageService.sendMessage.onMessage', response.chunks);
          set(state => ({
            chunks: response.chunks
          }));
          if (typeof onMessage === 'function') {
            onMessage(response);
          }
        },
        // onEnd
        response => {
          // console.log('messageService.sendMessage.onEnd', response);
          const chunks = [...response.chunks];
          const assistantMessage = {
            chunks,
            content: '',
            createdAt: new Date().toISOString(),
            messageId: response.messageId,
            role: 'assistant',
            threadId: response.threadId
          };
          const responseMessages = [...messages, assistantMessage];
          if (messageService && messageService.shouldAddFormRequest(responseMessages)) {
            chunks.push({
              type: 'formRequest'
            });
          }
          // filtering history
          const validChunks = chunks.filter(x => typeof x.type === 'string' && !['header', 'info', 'end', 'formRequest', 'formRecap', 'formRecapSuccess', 'formRecapError'].includes(x.type));
          const parsedChunks = [];
          validChunks.reduce((p, c) => {
            const firstChunk = p.length > 1 && p[p.length - 2];
            const secondChunk = p.length > 1 && p[p.length - 1];
            if (firstChunk && secondChunk && c.type === 'string' && firstChunk.type === 'string' && secondChunk.type === 'string') {
              secondChunk.content += c.content;
            } else {
              p.push(Object.assign({}, c));
            }
            return p;
          }, parsedChunks);
          assistantMessage.content = parsedChunks.map(x => JSON.stringify(x)).join(',') + ',';
          // console.log('messageService.sendMessage.onEnd', assistantMessage);
          const history = messagesToHistory(responseMessages);
          const historyItem = history[0];
          addItemToHistory({
            threadId: historyItem.threadId,
            messageId: historyItem.messageId
          });
          let newHistory = [historyItem, ...state.history];
          if (theme.mode !== 'page') {
            newHistory = newHistory.reduce((p, c) => {
              if (p.findIndex(x => x.threadId === c.threadId) === -1) {
                p.push(c);
              }
              return p;
            }, []);
          }
          set(state => ({
            chunks: undefined,
            history: newHistory,
            message: assistantMessage,
            messages: responseMessages,
            streaming: false,
            threadId: response.threadId
          }));
          speech.reader.end();
          if (typeof onEnd === 'function') {
            onEnd(response);
          }
        },
        // onChunk
        chunks => {
          // console.log('onChunk', chunks);
          const header = chunks.find(x => isObject(x) && x.type === 'header');
          if (header) {
            set(state => ({
              threadId: header.threadId
            }));
            // console.log('onChunk', header);
          }
          const texts = chunksToTexts(chunks);
          texts.forEach(text => speech.reader.add(text));
        });
      }),
      abort: () => {
        speech.cancel();
        if (messageService) {
          messageService.abort();
          messageService = undefined;
        }
      },
      setCurrentContext: currentContext => {
        const {
          systemContext
        } = get();
        set(state => ({
          currentContext,
          systemContext: Object.assign(Object.assign({}, systemContext), currentContext.context)
        }));
      },
      setVars: vars => set(state => ({
        vars: Object.assign({}, state.vars, vars)
      })),
      setEmbed: (element, active) => {
        const embed = get().embed;
        if (!active && embed !== element) {
          return;
        }
        set(state => {
          return {
            embed: active ? element : undefined
          };
        });
      },
      setPrompt: prompt => {
        var _a;
        const promptTokenLimit = ((_a = get().app) === null || _a === void 0 ? void 0 : _a.contents.promptTokenLimit) || 8000;
        const promptWordsLimit = promptTokenLimit / 2;
        let words = 0,
          lastCharIsNonChar = false;
        const sanitizedPrompt = prompt.split('').reduce((p, c) => {
          const isNonChar = Boolean(c.match(/\W/));
          if (isNonChar && !lastCharIsNonChar) {
            words++;
          }
          lastCharIsNonChar = isNonChar;
          if (words < promptWordsLimit) {
            return p + c;
          }
          return p;
        }, '');
        if (sanitizedPrompt.length < prompt.length) {
          toast('Prompt token limit exceeded!');
        }
        set(state => ({
          prompt: sanitizedPrompt
        }));
      },
      hasSpeechSynthesisSupport: () => {
        return speech.hasTextToSpeechSupport;
      },
      hasSpeechRecognitionSupport: () => {
        return speech.hasSpeechToTextSupport;
      },
      toggleSpeak: () => {
        set(state => {
          const speakEnabled = !state.speakEnabled;
          speech.setEnabled(speakEnabled);
          return {
            speakEnabled
          };
        });
      },
      toggleAside: () => {
        set(state => {
          const asideEnabled = !state.asideEnabled;
          return {
            asideEnabled
          };
        });
      },
      recognizeStart: () => {
        get();
        speech.recognizeStart();
      },
      recognizeStop: () => {
        get();
        speech.recognizeStop();
      },
      hasUploadSupport: () => {
        var _a;
        const state = get();
        return ((_a = state.app) === null || _a === void 0 ? void 0 : _a.contents.enableUpload) === true;
      },
      addFile: event => __awaiter(void 0, void 0, void 0, function* () {
        var _c;
        const {
          maxFileSize
        } = get();
        const files = (_c = event.target) === null || _c === void 0 ? void 0 : _c.files;
        // console.log('useLlm.addFile', files, Array.isArray(files));
        try {
          if (files && files.length > 0) {
            const file = files[0];
            if (!file) {
              console.log('useLlm.addFile.error', 'file not found');
              return;
            }
            // const app = get().app;
            if (file.size > maxFileSize) {
              toast(`Max file size exceeded ${formatBytes(maxFileSize)}`);
              return;
            }
            const base64 = file.type.indexOf('image') === 0 ? yield fileToResizedBase64(file) : undefined;
            event.target.value = null;
            // console.log('useLlm.addFile.base64', base64);
            const upload = {
              file,
              base64
            };
            // console.log('useLlm.addFile.success', upload);
            return set({
              upload
            });
          } else {
            console.log('useLlm.addFile.error', 'file not found');
          }
        } catch (error) {
          console.log('useLlm.addFile.error', 'cannot parse file');
        }
      }),
      removeFile: () => {
        return set({
          upload: undefined
        });
      },
      formRequest: choice => {
        const choiceMessage = choice ? label('llm.formRequestYes') : label('llm.formRequestNo');
        const userMessage = {
          chunks: [{
            type: 'string',
            content: choiceMessage
          }],
          content: choiceMessage,
          role: 'user'
        };
        const messages = [userMessage];
        if (choice) {
          const assistantMessage = {
            chunks: [{
              type: 'formRecap'
            }],
            content: '',
            role: 'assistant',
            createdAt: new Date().toISOString()
          };
          messages.push(assistantMessage);
        }
        set(state => ({
          messages: [...state.messages, ...messages]
        }));
      },
      formRecap: error => {
        if (error) {
          console.warn('useLlm.formRecap', error);
        }
        const messages = [];
        const assistantMessage = {
          chunks: [{
            type: error ? 'formRecapError' : 'formRecapSuccess'
          }],
          content: '',
          role: 'assistant',
          createdAt: new Date().toISOString()
        };
        messages.push(assistantMessage);
        set(state => ({
          messages: [...state.messages, ...messages]
        }));
      },
      decorateUrl: (item, defaultUrl) => __awaiter(void 0, void 0, void 0, function* () {
        let url;
        if (typeof decorateUrl === 'function') {
          url = (yield decorateUrl(item)) || undefined;
        }
        return url || defaultUrl || '#';
      }),
      // remove a response or a thread from history and StoredHistory
      removeHistory: history => {
        // console.log('removeHistory', history);
        const state = get();
        const mode = state.theme.mode;
        removeItemFromHistory(mode === 'page' ? {
          threadId: history.threadId,
          messageId: history.messageId
        } : {
          threadId: history.threadId
        });
        const newHistory = state.history.filter(x => mode === 'page' ? x.messageId !== history.messageId : x.threadId !== history.threadId);
        set(state => ({
          history: newHistory,
          storedHistory: historyToStoredHistory(newHistory)
        }));
      },
      // retrieve a response or a thread from history
      setHistory: history => {
        // console.log('setHistory', history);
        set(state => ({
          threadId: history.threadId,
          message: history.assistant,
          messages: history.messages
          /*
          messages: state.messages.findIndex(x => x.messageId === history.messageId) === -1 ?
            [...state.messages, history.user, history.assistant] :
            state.messages,
          */
        }));
      },
      onAction: item => __awaiter(void 0, void 0, void 0, function* () {
        if (typeof onAction === 'function') {
          return yield onAction(item);
        }
        return;
      }),
      clear: () => {
        const {
          abort
        } = get().actions;
        abort();
        set(state => ({
          message: undefined,
          messages: [],
          chunks: undefined,
          threadId: undefined,
          streaming: false
        }));
      }
    }
  }), {
    name: storeKey,
    storage: createJSONStorage(() => props.storageMode === 'session' ? sessionStorage : localStorage),
    onRehydrateStorage: () => () => {
      useStore.setState({
        hydrated: true
      });
    },
    merge: (persistedState, currentState) => {
      if (persistedState.app) {
        currentState.app = Object.assign({}, persistedState.app);
      }
      if (persistedState.threadId && currentState.storageMode === 'session') {
        currentState.threadId = persistedState.threadId;
      }
      currentState.storedHistory = persistedState.storedHistory && [...persistedState.storedHistory];
      currentState.speakEnabled = persistedState.speakEnabled || false;
      speech.enabled = currentState.speakEnabled;
      /*
      if (persistedState.messages) {
        currentState.messages = [...persistedState.messages];
      }
      */
      currentState.hydrated = true;
      // console.log('currentState', currentState);
      return currentState;
    },
    partialize: state => {
      const enabledKeys = state.storageMode === 'session' ? ['locale', 'speakEnabled', 'threadId'] : ['locale', 'speakEnabled'];
      const store = Object.fromEntries(Object.entries(state).filter(_ref => {
        let [key] = _ref;
        return enabledKeys.includes(key);
      }));
      store.storedHistory = historyToStoredHistory(state.history);
      // console.log('history', store.storedHistory);
      return store;
    }
  }));
  return useStore;
};
function messagesToHistory(messages) {
  const history = [...messages].reverse().reduce((p, c) => {
    let currentItem;
    if (c.role === 'assistant') {
      currentItem = {
        messages,
        threadId: c.threadId,
        messageId: c.messageId,
        createdAt: c.createdAt,
        assistant: Object.assign(Object.assign({}, c), {
          text: c.chunks.filter(x => x.type === 'text').map(x => x.text).join(' ')
        })
      };
      p.push(currentItem);
    } else if (p.length > 0) {
      currentItem = {
        user: Object.assign(Object.assign({}, c), {
          text: c.chunks.filter(x => x.type === 'text').map(x => x.text).join(' ')
        })
      };
      Object.assign(p[p.length - 1], currentItem);
    }
    return p;
  }, []);
  // console.log('useLlm.messagesToHistory', history);
  return history.filter(x => x.threadId && x.messageId);
}
function historyToStoredHistory(history) {
  return history.map(x => ({
    threadId: x.threadId,
    messageId: x.messageId
  }));
}
let sharedLlmStore;
function createSharedLlmStore(props) {
  if (!sharedLlmStore) {
    sharedLlmStore = createLlmStore(props);
  }
  return sharedLlmStore;
}
const LlmUseContext = /*#__PURE__*/createContext(null);
function LlmProvider(_a) {
  var {
      children
    } = _a,
    props = __rest(_a, ["children"]);
  const label = useLabel();
  if (props.embedded) {
    props.dismissable = false;
    props.opened = true;
  }
  if (props.preview) {
    props.dismissable = false;
    props.opened = true;
    props.skipCustomIntro = true;
  }
  const storeRef = useRef();
  if (!storeRef.current) {
    storeRef.current = createSharedLlmStore(Object.assign(Object.assign({}, props), {
      label
    }));
  }
  return storeRef.current && jsx(LlmUseContext.Provider, {
    value: storeRef.current,
    children: jsx(LlmViewProvider, Object.assign({}, props, {
      children: children
    }))
  });
}
function useLlm(selector) {
  const store = useContext(LlmUseContext);
  if (!store) throw new Error('Missing LlmContext.Provider in the tree');
  return useStore(store, selector);
}

export { LlmProvider, LlmUseContext, historyToStoredHistory, messagesToHistory, useLlm };
//# sourceMappingURL=useLlm.js.map
